import { UsCpscComplianceType } from '../types/shipment';
import { isComplianceRequired } from './euCompliance';

/**
 * Normalizes a country identifier or name and checks whether it corresponds to the United States.
 * Supports all common aliases: US, USA, United States, United States of America, U.S., U.S.A., America.
 */
export function isUSDestination(countryInput?: any): boolean {
  if (!countryInput) return false;

  let raw = '';
  if (typeof countryInput === 'string') {
    raw = countryInput;
  } else if (typeof countryInput === 'object') {
    raw = countryInput.country?.name || countryInput.name || countryInput.country || '';
  }

  if (typeof raw !== 'string') return false;

  const normalized = raw
    .trim()
    .toLowerCase()
    .replace(/\./g, '') // strip dots: 'u.s.' -> 'us', 'u.s.a.' -> 'usa'
    .replace(/\s+/g, ' '); // collapse spaces

  const US_ALIASES = new Set(['us', 'usa', 'united states', 'united states of america', 'america']);

  return US_ALIASES.has(normalized);
}

export const CPSC_DISCLAIM_CODE_OPTIONS = [
  { display: 'Not regulated by CPSC', value: 'Not regulated by CPSC' },
  { display: 'CPSC enforcement discretion (rare)', value: 'CPSC enforcement discretion (rare)' },
];

export const CPSC_DEFAULT_DISCLAIM_CODE = 'Not regulated by CPSC';

/**
 * Normalizes legacy CPSC disclaim code values ('A' / 'B') to the new full display strings.
 */
export function normalizeCpscDisclaimCode(code?: string | null): string {
  if (!code) return CPSC_DEFAULT_DISCLAIM_CODE;
  const trimmed = code.trim();
  if (trimmed === 'A') return 'Not regulated by CPSC';
  if (trimmed === 'B') return 'CPSC enforcement discretion (rare)';
  return trimmed;
}

export const CPSC_PRODUCT_ID_TYPE_OPTIONS = [
  { display: 'SKU', value: 'SKU' },
  { display: 'Part number', value: 'Part number' },
  { display: 'Registered number', value: 'Registered number' },
  { display: 'Other', value: 'Other' },
];

export const defaultCpscComplianceState: UsCpscComplianceType = {
  beingSold: true,
};

export type CpscFieldKey =
  | 'cpscBeingSold'
  | 'cpscDisclaimCode'
  | 'cpscCertificateFiled'
  | 'cpscProductIdRegistry'
  | 'cpscCertificateVersionId'
  | 'cpscCertifierId'
  | 'cpscProductId'
  | 'cpscProductIdType';

/**
 * Robust boolean parser handling booleans, strings ('true', 'false', 'yes', 'no'), and numbers (1, 0).
 */
export function parseBoolean(val: any): boolean | undefined {
  if (val === true || val === 'true' || val === 'yes' || val === 'Yes' || val === 1 || val === '1') return true;
  if (val === false || val === 'false' || val === 'no' || val === 'No' || val === 0 || val === '0') return false;
  return undefined;
}

/**
 * Returns the exact set of required US CPSC field keys for the active branch.
 */
export function getActiveUsCpscRequiredFields(
  cpsc?: UsCpscComplianceType | null,
  isUS = true,
  isRequired?: boolean,
): CpscFieldKey[] {
  if (!isUS) return [];
  const complianceRequired = typeof isRequired === 'boolean' ? isRequired : isComplianceRequired();
  if (!complianceRequired) return [];

  const effectiveCpsc = cpsc || defaultCpscComplianceState;
  const beingSold = parseBoolean(effectiveCpsc.beingSold) ?? true;

  if (beingSold === false) {
    return ['cpscDisclaimCode'];
  }

  const certificateFiled = parseBoolean(effectiveCpsc.certificateFiled);
  if (certificateFiled === undefined) {
    return ['cpscCertificateFiled'];
  }

  if (certificateFiled === true) {
    return ['cpscCertificateFiled', 'cpscProductIdRegistry', 'cpscCertificateVersionId', 'cpscCertifierId'];
  }

  return ['cpscCertificateFiled', 'cpscProductId', 'cpscProductIdType'];
}

export interface CpscValidationResult {
  isValid: boolean;
  errors: { [key: string]: string };
}

/**
 * Validates US CPSC compliance fields according to the active conditional branch.
 */
export function validateCpscCompliance(
  cpsc?: UsCpscComplianceType | null,
  isUS = true,
  isRequired?: boolean,
): CpscValidationResult {
  const errors: { [key: string]: string } = {};
  if (!isUS) {
    return { isValid: true, errors: {} };
  }

  const complianceRequired = typeof isRequired === 'boolean' ? isRequired : isComplianceRequired();
  if (!complianceRequired) {
    return { isValid: true, errors: {} };
  }

  const effectiveCpsc = cpsc || defaultCpscComplianceState;
  const activeRequiredFields = getActiveUsCpscRequiredFields(effectiveCpsc, isUS, isRequired);

  for (const field of activeRequiredFields) {
    switch (field) {
      case 'cpscBeingSold': {
        const beingSold = parseBoolean(effectiveCpsc.beingSold);
        if (beingSold === undefined) {
          errors.cpscBeingSold = 'Please specify if this item is being sold';
        }
        break;
      }
      case 'cpscDisclaimCode': {
        const disclaim = effectiveCpsc.disclaimCode?.trim();
        if (!disclaim) {
          errors.cpscDisclaimCode = 'Please select a Disclaim code';
        }
        break;
      }
      case 'cpscCertificateFiled': {
        const certFiled = parseBoolean(effectiveCpsc.certificateFiled);
        if (certFiled === undefined) {
          errors.cpscCertificateFiled = 'Please specify if the certificate is filed in the CPSC Product Registry';
        }
        break;
      }
      case 'cpscProductIdRegistry': {
        if (!effectiveCpsc.productIdRegistry?.trim()) {
          errors.cpscProductIdRegistry = 'Product ID (registry) is required';
        }
        break;
      }
      case 'cpscCertificateVersionId': {
        if (!effectiveCpsc.certificateVersionId?.trim()) {
          errors.cpscCertificateVersionId = 'Certificate Version ID is required';
        }
        break;
      }
      case 'cpscCertifierId': {
        if (!effectiveCpsc.certifierId?.trim()) {
          errors.cpscCertifierId = 'Certifier ID is required';
        }
        break;
      }
      case 'cpscProductId': {
        if (!effectiveCpsc.productId?.trim()) {
          errors.cpscProductId = 'Product ID is required';
        }
        break;
      }
      case 'cpscProductIdType': {
        if (!effectiveCpsc.productIdType?.trim()) {
          errors.cpscProductIdType = 'Product ID type is required';
        }
        break;
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Builds the sanitized US CPSC payload, omitting unselected branch keys.
 */
export function buildCpscPayload(cpsc?: UsCpscComplianceType | null, isUS = true): UsCpscComplianceType | undefined {
  if (!isUS) return undefined;
  const effectiveCpsc = cpsc || defaultCpscComplianceState;

  const beingSold = parseBoolean(effectiveCpsc.beingSold) ?? true;
  if (!beingSold) {
    const res: UsCpscComplianceType = {
      beingSold: false,
      disclaimCode: normalizeCpscDisclaimCode(effectiveCpsc.disclaimCode),
    };
    if (effectiveCpsc.additionalContext?.trim()) {
      res.additionalContext = effectiveCpsc.additionalContext.trim();
    }
    return res;
  }

  const certificateFiled = parseBoolean(effectiveCpsc.certificateFiled);
  if (certificateFiled === true) {
    return {
      beingSold: true,
      certificateFiled: true,
      productIdRegistry: effectiveCpsc.productIdRegistry?.trim() || '',
      certificateVersionId: effectiveCpsc.certificateVersionId?.trim() || '',
      certifierId: effectiveCpsc.certifierId?.trim() || '',
    };
  }

  return {
    beingSold: true,
    certificateFiled: false,
    productId: effectiveCpsc.productId?.trim() || '',
    productIdType: effectiveCpsc.productIdType?.trim() || 'SKU',
  };
}
